import { msg } from "../locales/translate";
import { strToU8, zipSync } from "fflate";
import type { IStatementRow } from "src/types/statement";

export const STATEMENT_TOTALS_COLUMNS = [
	{ value: "currency", get label() { return msg("currency"); } },
	{ value: "total_amount", get label() { return msg("turnover_8ecaf1d7"); } },
	{ value: "total_refunds", get label() { return msg("refunds_0942c179"); } },
	{ value: "total_chargebacks", get label() { return msg("chargebacks_655ad8a5"); } },
	{ value: "total_oct", get label() { return msg("oct"); } },
	{ value: "total_failed", get label() { return msg("failed_031a8f0f"); } },
	{ value: "total_rollings", get label() { return msg("rollings_bd316df6"); } },
	{ value: "total_release_rollings", get label() { return msg("released_f33deea9"); } },
	{ value: "total_fees", get label() { return msg("fees_6d80486c"); } },
	{ value: "setup", get label() { return msg("setup_7013af4c"); } },
	{ value: "monthly", get label() { return msg("monthly_9b11f6b7"); } },
	{ value: "withdrawal", get label() { return msg("withdrawal_d2987dcd"); } },
	{ value: "total_net", get label() { return msg("net"); } },
] as const;

export function statementTotalsRows(statement: IStatementRow, clientName?: string) {
	const totals = statement.totals ?? {};
	const columns = STATEMENT_TOTALS_COLUMNS.filter(({ value }) =>
		(value !== "setup" && value !== "monthly") || Object.values(totals).some(row => Number(row[value] ?? 0) !== 0));
	return [
		["client_name", "period_start", "period_end", msg("creationDate"), ...columns.map(column => column.label)],
		...Object.entries(totals).map(([currency, row]) => [
			statement.data?.client_name ?? statement.client_name ?? clientName ?? "",
			statement.period_start ?? "", statement.period_end ?? "", statement.calculated_at ?? "",
			...columns.map(({ value }) => value === "currency" ? currency : row[value] ?? 0),
		]),
	];
}

type Rows = (string | number)[][];

export function statementsListTotalsRows(statements: IStatementRow[]): Rows {
	return [
		["client_name", "period_start", "period_end", "calculated_at", "statement_id", "status", ...STATEMENT_TOTALS_COLUMNS.map(column => column.label)],
		...statements.flatMap(statement => {
			const metadata = [statement.data?.client_name ?? statement.client_name ?? msg("templateClient_2fb386d5", { v0: statement.client_id }),
				statement.period_start ?? "", statement.period_end ?? "", statement.calculated_at ?? "", String(statement.id), statement.status];
			const totals = Object.entries(statement.totals ?? {});
			// Keep uncalculated statements in the export without inventing zero balances.
			if (totals.length === 0) return [[...metadata, ...STATEMENT_TOTALS_COLUMNS.map(() => "")]];
			return totals.map(([currency, row]) => [...metadata,
				...STATEMENT_TOTALS_COLUMNS.map(({ value }) => value === "currency" ? currency : row[value] ?? 0)]);
		}),
	];
}

export function statementTotalsCsv(rows: Rows) {
	return "\uFEFF" + rows.map(row => row.map(value => {
		const text = typeof value === "string" && /^[\s]*[=+@-]/.test(value) ? `'${value}` : String(value);
		return `"${text.replace(/"/g, '""')}"`;
	}).join(",")).join("\r\n");
}

// A single-sheet Open XML workbook; amounts remain numeric and strings never become formulas.
export function statementTotalsXlsx(rows: Rows) {
	const xml = (value: string) => value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
	const sheet = rows.map((row, index) => `<row r="${index + 1}">${row.map(value =>
		typeof value === "number" && Number.isFinite(value)
			? `<c t="n"><v>${value}</v></c>`
			: `<c t="inlineStr"><is><t xml:space="preserve">${xml(String(value))}</t></is></c>`).join("")}</row>`).join("");
	const files = {
		"[Content_Types].xml": '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>',
		"_rels/.rels": '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
		"xl/workbook.xml": '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Totals" sheetId="1" r:id="rId1"/></sheets></workbook>',
		"xl/_rels/workbook.xml.rels": '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>',
		"xl/worksheets/sheet1.xml": `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols><col min="1" max="1" width="32" customWidth="1"/><col min="2" max="4" width="24" customWidth="1"/><col min="5" max="${Math.max(5, rows[0]?.length ?? 5)}" width="18" customWidth="1"/></cols><sheetData>${sheet}</sheetData></worksheet>`,
	};
	return zipSync(Object.fromEntries(Object.entries(files).map(([path, content]) => [path, strToU8(content)])));
}
